import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { logSentryError } from '@node-space/utils'
import { useMounted } from 'hooks/useMounted'
import { IWalletActionApiType } from 'pages/Wallets/hooks/useWalletActionApi'
import { getWalletLimits, postQuoteEstimate } from 'services/WalletsService'
import { Quote } from 'types/payments'
import { CurrencyLimits } from 'types/types'
import { WalletTabProps } from '../Modal/WalletModal'
import ConvertComplete from './ConvertComplete'
import ConvertConfirm from './ConvertConfirm'
import ConvertForm from './ConvertForm'

export type ConvertSteps = 'form' | 'confirm' | 'complete'

export type ConvertFormType = {
  walletId?: string
  toWalletId?: string
  amount?: string | number
}

export type ConvertActionProps = WalletTabProps &
  IWalletActionApiType<ConvertFormType, ConvertSteps> & {
    withdrawalLimits?: CurrencyLimits
    quote: Quote
    setQuote: Dispatch<SetStateAction<Quote>>
    convertCurrency: string
    setConvertCurrency: Dispatch<SetStateAction<string>>
    quoteEstimateLoading: boolean
    quoteEstimate: Quote
    fetchQuoteExchangeRate: () => void
    shouldUseMax: boolean
    shouldUseMin: boolean
    setShouldUseMax: Dispatch<SetStateAction<boolean>>
    setShouldUseMin: Dispatch<SetStateAction<boolean>>
  }

type ConvertProps = WalletTabProps & IWalletActionApiType<ConvertFormType, ConvertSteps>

export const Convert = ({
  setTabVisible,
  setLoading,
  loading,
  step,
  form,
  wallets,
  ...otherProps
}: ConvertProps) => {
  const isMounted = useMounted()

  const [shouldUseMax, setShouldUseMax] = useState(false)
  const [shouldUseMin, setShouldUseMin] = useState(false)
  const [quote, setQuote] = useState<Quote>()

  const [quoteEstimateLoading, setQuoteEstimateLoading] = useState(false)
  const [quoteEstimate, setQuoteEstimate] = useState<Quote>(undefined)
  const [withdrawalLimits, setWithdrawalLimits] = useState<CurrencyLimits | null>()
  const [convertCurrency, setConvertCurrency] = useState<string>()

  const formValues = form.watch()

  /**
   * Fetch new quote on from/to wallet change
   */
  const fetchQuoteExchangeRate = () => {
    if (formValues.walletId && formValues.toWalletId) {
      const fromWallet = wallets.find(x => x.id.toString() === formValues.walletId)
      const toWallet = wallets.find(x => x.id.toString() === formValues.toWalletId)

      const fromWalletCurrency = (
        fromWallet ? fromWallet.currency.code : formValues.walletId
      )?.toString()
      const toWalletCurrency = (
        toWallet ? toWallet.currency.code : formValues.toWalletId
      )?.toString()

      const sameCurrency =
        quoteEstimate?.from === fromWalletCurrency && quoteEstimate?.to === toWalletCurrency

      if (fromWalletCurrency && toWalletCurrency && !sameCurrency) {
        setQuoteEstimateLoading(true)
        setQuoteEstimate(undefined)
        postQuoteEstimate({ from: fromWalletCurrency, to: toWalletCurrency })
          .then(response => {
            const formState = form.getValues()
            // only update state if latest selected wallet matches request
            if (formState.walletId === fromWallet.id.toString()) {
              setQuoteEstimate(response.data)
            }
          })
          .catch(err => {
            setQuoteEstimate(null)
            logSentryError('Error from Convert - fetchQuoteExchangeRate', err, {
              fromCurrency: fromWalletCurrency,
              toCurrency: toWalletCurrency,
            })
          })
          .finally(() => {
            setQuoteEstimateLoading(false)
          })
      }
    }
  }

  const props = {
    form,
    withdrawalLimits,
    wallets,
    quote,
    setQuote,
    convertCurrency,
    setConvertCurrency,
    quoteEstimateLoading,
    quoteEstimate,
    fetchQuoteExchangeRate,
    shouldUseMax,
    shouldUseMin,
    setShouldUseMax,
    setShouldUseMin,
    setTabVisible,
    setLoading,
    loading,
    step,
    ...otherProps,
  }

  /**
   * Get withdrawal limits for selected curreny code
   */
  useEffect(() => {
    if (formValues.walletId && wallets?.length) {
      const wallet = wallets.find(x => x.id.toString() === formValues.walletId)

      if (wallet) {
        setLoading(true)
        setWithdrawalLimits(null)

        // get withdrawal limits for selected currency
        getWalletLimits(wallet.currency.code, wallet.id)
          .then(currencyLimitsResponse => {
            const limits = currencyLimitsResponse?.data
            if (isMounted) {
              limits && setWithdrawalLimits(limits)
            }
          })
          .catch(err => {
            logSentryError('Error from Convert - getWalletLimits', err)
          })
          .finally(() => {
            setLoading(false)
          })
      }
    }
  }, [formValues.walletId, wallets])

  useEffect(() => {
    if (formValues.toWalletId) {
      form.clearErrors('amount')
      const wallet = wallets.find(x => x.id.toString() === formValues.toWalletId)
      setConvertCurrency(wallet ? wallet.currency.code : formValues.toWalletId)
    }
  }, [formValues.toWalletId])

  useEffect(() => {
    fetchQuoteExchangeRate()
  }, [formValues.walletId, formValues.toWalletId, form.formState?.errors])

  useEffect(() => {
    setTabVisible && setTabVisible(step === 'form')
  }, [step])

  /**
   * Switch active component based on 'step' value
   */
  const renderStep = () => {
    switch (step) {
      case 'form':
        return <ConvertForm {...props} />
      case 'confirm':
        return <ConvertConfirm {...props} />
      case 'complete':
        return <ConvertComplete {...props} />
    }
  }

  return renderStep()
}
